import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  extractMaterialEdges,
  getNextLinearMethodNode,
  getOrderedDownstreamLinearMethodNodes,
} from 'procedures/design/flowchartManagementUtils';
import {ProcedureFlowchart} from 'procedures/ProcedureDomainTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';

import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from '../ProcedureValidationTypes';

export function validateProcedureMaterialEdges(
  flowchart: ProcedureFlowchart,
  procedureType: ProcedureType
): Array<ProcedureValidationError> {
  const methodUIString = getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
    plural: true,
    stringCase: StringCase.LOWER,
  });

  return extractMaterialEdges(flowchart.edges).flatMap((edge) => {
    const errors: Array<ProcedureValidationError> = [];
    const baseErrorObject = {
      primaryObject: {id: edge.id, objectType: ProcedureValidationObjectType.MATERIAL_EDGE},
      parentObjects: [
        {id: edge.fromMethodCid, objectType: ProcedureValidationObjectType.METHOD},
        {id: edge.toMethodCid, objectType: ProcedureValidationObjectType.METHOD},
      ],
    };

    // Check for upstream (backward) material edges
    const isUpstreamEdge =
      getOrderedDownstreamLinearMethodNodes(flowchart, edge.fromMethodCid).find(
        (node) => node.cid === edge.toMethodCid
      ) == null;
    if (isUpstreamEdge) {
      errors.push({
        errorMessage: `Materials can only flow from left to right between ${methodUIString}`,
        type: ProcedureValidationErrorType.UPSTREAM_MATERIAL_EDGE,
        ...baseErrorObject,
      });
    }

    // Check for indirect material edges (skipping intermediate methods)
    const nextNode = getNextLinearMethodNode(flowchart, edge.fromMethodCid);
    // Here we exclude the upstream edges because we want to prevent the case where an upstream edge is considered
    // indirect when the next node from the source method is not the target method. Adding this filter, will shift
    // the focus towards indirect material edges being downstream material edges that are skipping a downstream method.
    const isIndirectEdge = nextNode != null && !isUpstreamEdge && nextNode.cid !== edge.toMethodCid;
    if (isIndirectEdge) {
      errors.push({
        errorMessage: `Materials can only flow between adjacent ${methodUIString}`,
        type: ProcedureValidationErrorType.INDIRECT_MATERIAL_EDGE,
        ...baseErrorObject,
      });
    }

    return errors;
  });
}
